import type { FeaturedContent } from '../types/featured';

const DATA_STORAGE_KEY = 'brixpos-mock-data';

export interface MockData {
  featured: FeaturedContent;
}

let store: MockData | null = null;
let loadPromise: Promise<MockData> | null = null;

async function fetchSeedData(): Promise<MockData> {
  const res = await fetch('/mock.json');
  if (!res.ok) throw new Error('Failed to load mock.json');
  return (await res.json()) as MockData;
}

function mergeMockData(partial: Partial<MockData>, seed: MockData): MockData {
  return {
    featured: partial.featured ?? seed.featured,
  };
}

function isCompleteStore(data: Partial<MockData>): data is MockData {
  return Boolean(
    data.featured,
  );
}

function persistMockStore(data: MockData): void {
  store = data;
  sessionStorage.setItem(DATA_STORAGE_KEY, JSON.stringify(store));
}

export async function loadMockStore(): Promise<MockData> {
  if (store && isCompleteStore(store)) return store;

  if (store && !isCompleteStore(store)) {
    loadPromise = null;
  }

  if (!loadPromise) {
    loadPromise = (async () => {
      const seed = await fetchSeedData();
      const cached = sessionStorage.getItem(DATA_STORAGE_KEY);
      const partial: Partial<MockData> = store ?? (cached ? JSON.parse(cached) : {});
      const merged = mergeMockData(partial, seed);
      persistMockStore(merged);
      return merged;
    })();
  }

  return loadPromise;
}

export async function getMockStore(): Promise<MockData> {
  return loadMockStore();
}

export async function updateMockStore(
  updater: (data: MockData) => void,
): Promise<MockData> {
  const data = await loadMockStore();
  updater(data);
  persistMockStore(data);
  return data;
}

export function resetMockStore(): void {
  sessionStorage.removeItem(DATA_STORAGE_KEY);
  store = null;
  loadPromise = null;
}

export class MockApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MockApiError';
  }
}
